import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DownloadIcon from "../../../assets/images/icons/DownloadIcon.tsx";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import { BriaImageProps } from "../../../components/common/Galleries/BriaImage";
import { useAppStore } from "../../../hooks/useStores";
import LabImageResult from "../../../layout/LabsLayout/Results/LabImageResult/LabImageResult";
import { PlaygroundImage, PlaygroundResult } from "../../../models/image-to-image";
import { TextToImageConfigType } from "../../../models/text-to-image";
import useImageUtils from "../../../utils/useImageUtils";
import PlaygroundImageOverlay from "../../Playground/Images/Overlay/PlaygroundImageOverlay";
import PromptTextWithActions from "../../Playground/TextToImage/Generator/PromptTextWithActions/PromptTextWithActions";
import MaskEditorControlsRow from "../MaskEditor/ControlPanel/ControlPanel.tsx";
import InlineMaskEditor from "../MaskEditor/InlineMaskEditor.tsx";
import { FiboEditConfigType } from "../fibo-edit-store";
import styles from "./FiboEditResult.module.scss";
import ThumbnailsRow from "./ThumbnailsRow/ThumbnailsRow";

type Props = {
	playgroundResult: PlaygroundResult;
	extraResultInfo?: ReactNode;
	resultHeaderActions?: ReactNode;
};

const FiboEditResult = ({ playgroundResult, resultHeaderActions }: Props) => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.results" });
	const config = playgroundResult.config as FiboEditConfigType;
	const isUploadedImage = playgroundResult.id.startsWith("uploaded");
	const { playgroundStore, fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const maskEditorStore = fiboEditStore.maskEditorStore;
	const [maskEditorEnabled, setMaskEditorEnabled] = useState(false);
	const loading = !!playgroundResult.images.filter((image) => image.loading).length;
	const imageUtils = useImageUtils();
	const previousImageRef = useRef<string | undefined>(undefined);
	const previousResultIdRef = useRef<string | undefined>(undefined);
	const [downloading, setDownloading] = useState(false);
	const selectedImageRef = useRef<string | undefined>(undefined);
	const isSelected = playgroundStore.selectedResult?.id === playgroundResult.id;

	const handleMaskEditorToggle = useCallback(
		(enabled: boolean) => {
			setMaskEditorEnabled(enabled);
			if (enabled && selectedImageRef.current) {
				fiboEditStore.updateImage(selectedImageRef.current);
			}
		},
		[fiboEditStore],
	);

	// Update config.image when user selects a different image
	const handleImageSelect = useCallback(
		(image: PlaygroundImage, _index: number) => {
			if (image.url && image.url !== fiboEditStore.config.image && !isUploadedImage) {
				fiboEditStore.updateImage(image.url);
			}
		},
		[fiboEditStore, isUploadedImage],
	);

	useEffect(() => {
		const currentImage = config.image;
		const currentResultId = playgroundStore.selectedResult?.id;
		const imageChanged = previousImageRef.current !== undefined && previousImageRef.current !== currentImage;
		const resultChanged =
			previousResultIdRef.current !== undefined && previousResultIdRef.current !== currentResultId;

		if (imageChanged || resultChanged) {
			setMaskEditorEnabled(false);
		}
		previousImageRef.current = currentImage;
		previousResultIdRef.current = currentResultId;
	}, [config.image, playgroundStore.selectedResult?.id]);

	const renderImageOverlay = useCallback(
		(image: PlaygroundImage) => {
			if (maskEditorEnabled) return null;
			return <PlaygroundImageOverlay image={image} hideAiEditorButton />;
		},
		[maskEditorEnabled],
	);

	const getImageProps = useCallback(
		(image: PlaygroundImage): Partial<BriaImageProps> => ({
			fullScreenProps: {
				fileName: playgroundStore.getFileName(image),
				format: (image.config as TextToImageConfigType)?.format,
				compareTo: !isUploadedImage ? config.image : undefined,
			},
			showCompareButton: !isUploadedImage,
			hideFullScreenButton: true,
			hideApiReferenceButton: true,
			hideDownloadIcon: isUploadedImage,
			displayOverlay: isUploadedImage ? "off" : "customHover",
			downloadProps: { instantDownload: true },
		}),
		[playgroundStore, isUploadedImage, config.image],
	);

	const renderImageExtra = useCallback(
		(image: PlaygroundImage, _imageElement: HTMLImageElement | null, isSelected: boolean) => {
			// Update the ref whenever renderImageExtra is called with the current image
			if (isSelected) {
				selectedImageRef.current = image.url;
			}

			if (!maskEditorEnabled || !isSelected) return null;
			return (
				<InlineMaskEditor
					show={true}
					image={image.url}
					showCanvas={true}
					showToggle={false}
					initialMaskEditorEnabled={maskEditorEnabled}
					onMaskModeChange={handleMaskEditorToggle}
					onClose={() => setMaskEditorEnabled(false)}
				/>
			);
		},
		[maskEditorEnabled, handleMaskEditorToggle],
	);

	const footerControls = (
		<MaskEditorControlsRow
			brush={{
				size: maskEditorStore.brushSize,
				type: maskEditorStore.maskType,
				onChange: (size, type) => {
					maskEditorStore.setBrushSize(size);
					maskEditorStore.setMaskType(type);
				},
			}}
			onResetMask={maskEditorStore.resetBrushConfigs}
			maskEditorEnabled={maskEditorEnabled}
			onMaskEditorToggle={handleMaskEditorToggle}
			showToggle={true}
			className={styles.controlsRow}
			isImageLoading={loading}
		/>
	);

	const headerContent = useMemo(
		() => (
			<Box className={styles.headerContent}>
				<Box className={styles.buttonsWrapper}>
					<BriaButton
						buttonType={"textMedium"}
						disabled={loading}
						loading={downloading}
						className={styles.button}
						onClick={async () => {
							setDownloading(true);
							const imagesToDownload = playgroundResult.images
								.filter((image) => !image.loading && image.url)
								.map((image) => ({
									fileName: playgroundStore.getFileName(image),
									imageUrl: image.url,
								}));

							if (imagesToDownload.length > 0) {
								const zipName = `fibo-edit-${playgroundResult.id}`;
								await imageUtils.exportZipImages(imagesToDownload, zipName, "download");
							}
							setDownloading(false);
						}}
						{...{ startIcon: <DownloadIcon /> }}
					>
						{t("download")}
					</BriaButton>
				</Box>
				<ThumbnailsRow
					imageUrl={config.image}
					mask={config.mask}
					onImageClick={() => fiboEditStore.updateImage(config.image)}
					onMaskClick={() => {}}
				/>
				<PromptTextWithActions
					prompt={config?.prompt}
					promptClassName={styles.prompt}
					resultHeaderActions={resultHeaderActions}
				/>
			</Box>
		),
		[
			loading,
			downloading,
			playgroundResult.images,
			playgroundResult.id,
			playgroundStore,
			imageUtils,
			t,
			config.image,
			config.mask,
			config.prompt,
			fiboEditStore,
			resultHeaderActions,
		],
	);

	return (
		<LabImageResult
			playgroundResult={playgroundResult}
			headerContent={headerContent}
			footerControls={footerControls}
			renderImageOverlay={renderImageOverlay}
			renderImageExtra={renderImageExtra}
			getImageProps={getImageProps}
			fullScreenProps={{
				displayMode: "arrows",
				hideApiReferenceButton: isUploadedImage ? true : false,
				hideFeedbackButtons: isUploadedImage ? true : false,
				hideDownloadIcon: isUploadedImage ? true : false,
			}}
			className={styles.fiboEditResult}
			onImageSelect={handleImageSelect}
			isSelected={isSelected}
			anonymousFeature="labs_edit"
		/>
	);
};

export default observer(FiboEditResult);
